import { useGetAddonsDataQuery } from '@/src/api/rtkQuery/addonsApi'
import { useGetCountryDataQuery } from '@/src/api/rtkQuery/countryApi'
import { useGetItineraryDataQuery } from '@/src/api/rtkQuery/itineraryApi'
import { ContactDetailsFormProps } from '@/src/components/common/forms/contactDetailsForm'
import { PersonalDetailsFormProps } from '@/src/components/common/forms/personalDetailsForm'
import useClevertapForItinerary from '@/src/hooks/analytics/clevertap/itinerary'
import { useThemedToast } from '@/src/hooks/ui/useThemedToast'
import { getActiveStep, getCompletedSteps, setActiveStep } from '@/src/redux/slices/itinerarySlice'
import { useAppDispatch, useAppSelector } from '@/src/redux/store'
import { DeviceType } from '@/src/types/deviceType'
import {
    Customer,
    DocumentDetails,
    DocumentType,
    ItineraryStepNumberType,
    NonDocumentField,
    TaxDetails,
    Title,
    Traveler
} from '@/src/types/itinerary'
import { htmlSanitizer } from '@/src/utils/browser'
import pushToNewRelic from '@/src/utils/analytics/newRelic/pushToNewRelic'
import { isoStringToDateObject } from '@/src/utils/itinerary/dateTransformer'
import { saveTravelerDataLocal } from '@/src/utils/itinerary/travelerDataLocalStoreManager'
import * as TravelerDocumentUtils from '@/src/utils/itinerary/travelerDocumentToInputsSegregater'
import { takeScreenshot } from '@/src/utils/screenshot'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSmbMandatoryCheck from '../../addons/useSmbMandatoryCheck'
import useGetItineraryId from '../../useGetItineraryId'
import useItineraryUtils from '../../useItineraryUtils'
import { useContactDetails } from '../useContactDetails'
import { useSavedTraveler } from '../useSavedTraveler'
import { useTaxDetails } from '../useTaxDetails'
import { useTravelerState } from '../useTravelerState'
import { useUpdateItineraryAndPrepayment } from '../useUpdateitinearyAndPrepayment'
import { useValidationHelpers } from '../useValidationHelpers'
import { useValidationManager } from '../useValidationManager'

/** Sentinel error class — thrown after the toast is already shown, so the catch block knows not to double-toast. */
class PassportScanError extends Error {}

/**
 * Parses DD/MM/YYYY (the format the middle-end returns after converting MRZ YYMMDD)
 * into the {day, month, year} shape the traveler form expects.
 * Defined at module level — pure function with no hook dependencies.
 */
const parseMRZDate = (dateStr: string) => {
    if (!dateStr) return null
    const parts = dateStr.split('/')
    if (parts.length !== 3) return null
    const day = parseInt(parts[0], 10).toString()
    const monthIndex = parseInt(parts[1], 10) - 1
    const year = parts[2]
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ]
    if (monthIndex < 0 || monthIndex > 11) return null
    return { day, month: months[monthIndex], year }
}

const useAddTravelersStepStateManager = ({
    deviceType,
    stepNumber,
    defaultMobileCountryCode = '966'
}: {
    deviceType: DeviceType
    stepNumber: ItineraryStepNumberType
    defaultMobileCountryCode?: '966' | '971'
}) => {
    const { showError, showSuccess } = useThemedToast()
    const dispatch = useAppDispatch()
    const { triggerItineraryEvent } = useClevertapForItinerary()
    const itineraryId = useGetItineraryId()
    const activeStep = useAppSelector(getActiveStep)
    const completedSteps = useAppSelector(getCompletedSteps)
    const { getIsStepCompleted } = useItineraryUtils()
    const validationTranslate = useTranslations('validation')
    const passportScanTranslate = useTranslations('passportScan')
    const isStepActive = deviceType === 'MOBILE' ? true : activeStep === stepNumber
    const travelerStepEntryTimeRef = useRef<number | null>(null)
    const {
        passengers,
        customer,
        mandatoryFields,
        isItineraryLoading,
        isItineraryError,
        arrivalDate,
        departureDate,
        flights
    } = useGetItineraryDataQuery(
        {
            itineraryId
        },
        {
            selectFromResult: ({ data, isError, isLoading }) => ({
                passengers: data?.passengers || [],
                customer: (data?.customer || {}) as Customer,
                mandatoryFields: (data?.meta?.mandatoryFields || []) as (DocumentType | NonDocumentField)[],
                isItineraryLoading: isLoading || false,
                isItineraryError: isError || false,
                arrivalDate: data?.searchCriteria.arrivalDate
                    ? isoStringToDateObject(data?.searchCriteria.arrivalDate)
                    : null,
                departureDate: data?.searchCriteria.departDate
                    ? isoStringToDateObject(data?.searchCriteria.departDate)
                    : null,
                flights: data?.flights || []
            })
        }
    )
    const { isMealMandatory, isSeatMandatory } = useSmbMandatoryCheck()
    const { data: addonsData } = useGetAddonsDataQuery({ itineraryId })
    const showMandatorySmbPopUp = (isMealMandatory || isSeatMandatory) && !addonsData?.isAncillaryUpdated

    useEffect(() => {
        if (isStepActive) {
            travelerStepEntryTimeRef.current = Date.now()
        }
    }, [isStepActive])

    const documentTypes: DocumentType[] = ['NATIONALID', 'IQAMA', 'PASSPORT']
    const requiredDocuments = mandatoryFields
        .filter(field => documentTypes.includes(field as DocumentType))
        .sort((a, b) => {
            const aIndex = documentTypes.indexOf(a as DocumentType)
            const bIndex = documentTypes.indexOf(b as DocumentType)
            return aIndex - bIndex
        }) as DocumentType[]

    const isNationalityRequired = mandatoryFields.includes('NATIONALITY')
    const isDateOfBirthRequired = mandatoryFields.includes('DOB')
    const isNationalityRequiredToRenderInUserPersonalDetailsSection =
        isNationalityRequired && requiredDocuments.length === 0
    const isBirthCountryRequired = mandatoryFields.includes('BIRTH_COUNTRY')

    // Use modular hooks
    const {
        travelers,
        setTravelers,
        onChangeTravelerDetails,
        onChangeDocumentField,
        travelersIndexToPassengerTypeAndSerialNumberMap
    } = useTravelerState({
        mandatoryFields,
        passengers,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired,
        requiredDocuments,
        isBirthCountryRequired
    })

    const { data: countryData } = useGetCountryDataQuery()

    const { contactDetails, onChangeContactDetails } = useContactDetails(customer, defaultMobileCountryCode)

    const { taxDetails, onChangeTaxDetails } = useTaxDetails()

    const { travelerValidation, contactValidation, taxValidation } = useValidationManager({
        deviceType,
        mandatoryFields,
        passengers,
        requiredDocuments,
        isDateOfBirthRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        arrivalDate,
        departureDate,
        isBirthCountryRequired
    })

    const { validateAllForms, validateTraveler } = useValidationHelpers({
        travelers,
        travelerValidation,
        contactValidation,
        taxValidation,
        contactDetails,
        taxDetails,
        isBirthCountryRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired
    })

    const { onSavedTravelerSelect } = useSavedTraveler({
        travelers,
        setTravelers,
        validateTraveler,
        contactValidation,
        contactDetails,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isDateOfBirthRequired,
        isBirthCountryRequired
    })

    // onChangeTravelerDetails with validation
    const handleChangeTravelerDetails: PersonalDetailsFormProps['onChangeTravelerDetails'] = useCallback(
        (idx, field, value) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeTravelerDetails(idx, field, sanitizedValue)
            const fieldKey = `traveler_${idx}_${field}`
            const current = travelerValidation.validationState?.[fieldKey]
            if (current?.isError) {
                travelerValidation.clearFieldError(fieldKey)
            }
        },
        [onChangeTravelerDetails, travelerValidation]
    )

    const handleBlurTravelerDetails = useCallback(
        (idx: number, field: keyof Traveler, inputValue: any) => {
            const fieldKey = `traveler_${idx}_${field}`
            const value = htmlSanitizer(inputValue) ?? travelers[idx]?.[field]
            travelerValidation.validateField(fieldKey, value)
        },
        [travelerValidation, travelers]
    )

    // onChangeDocumentField with validation
    const handleChangeDocumentField = useCallback(
        (travelerIndex: number, documentType: DocumentType, field: string, value: any) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeDocumentField(travelerIndex, documentType, field, sanitizedValue)
            const fieldKey = `traveler_${travelerIndex}_${documentType}_${field}`
            const current = travelerValidation.validationState?.[fieldKey]
            if (current?.isError) {
                travelerValidation.clearFieldError(fieldKey)
            }
        },
        [onChangeDocumentField, travelerValidation]
    )

    const handlePassportScan = useCallback(async (idx: number, file: File) => {
        try {
            const formData = new FormData()
            formData.append('image', file)
            if (itineraryId) formData.append('itineraryId', itineraryId)

            const response = await fetch('/flight/search/v2/extract-passport', {
                method: 'POST',
                body: formData
            })

            if (!response.ok) {
                // Throw so PassportScan.tsx catches it and clears the preview
                const msg = passportScanTranslate('errorStatus', { status: response.status })
                showError(msg)
                throw new PassportScanError(msg)
            }

            const data = await response.json()
            pushToNewRelic('Air_UI_Action', {
                itineraryId,
                action_name: 'passport_upload',
                response: JSON.stringify(data)
            })

            if (data.success && data.data) {
                const pd = data.data

                // Personal details
                if (pd.firstName) handleChangeTravelerDetails(idx, 'firstName', pd.firstName)
                if (pd.lastName) handleChangeTravelerDetails(idx, 'lastName', pd.lastName)

                // Title — API derives Mr/Ms from the MRZ sex field
                const titlesMap: Record<string, Title> = {
                    mr: 'Mr',
                    ms: 'Ms',
                    mrs: 'Mrs',
                    mstr: 'Mstr',
                    miss: 'Miss'
                }
                const foundTitle = pd.title ? titlesMap[pd.title.toLowerCase()] : undefined
                if (foundTitle) handleChangeTravelerDetails(idx, 'title', foundTitle)

                // Switch to PASSPORT document type
                handleChangeTravelerDetails(idx, 'selectedDocumentType', 'PASSPORT')

                // Passport number
                if (pd.passportNumber) handleChangeDocumentField(idx, 'PASSPORT', 'docNumber', pd.passportNumber)

                // ISO Alpha-2 code → country name (for dropdowns)
                const getCountryName = (isoCode: string) => {
                    if (!isoCode) return null
                    return countryData?.find(c => c.countryCode === isoCode.toUpperCase())?.en ?? null
                }

                // Nationality + birth country (MRZ has no separate birth country field;
                // nationality is the best available proxy)
                if (pd.nationality) {
                    const countryName = getCountryName(pd.nationality)
                    if (countryName) {
                        if (isNationalityRequiredToRenderInUserPersonalDetailsSection) {
                            handleChangeTravelerDetails(idx, 'nationality', countryName)
                        }
                        if (isBirthCountryRequired) {
                            handleChangeTravelerDetails(idx, 'birthCountry', countryName)
                        }
                        handleChangeDocumentField(idx, 'PASSPORT', 'nationality', countryName)
                    }
                }

                // Issuing country
                if (pd.issuingState) {
                    const countryName = getCountryName(pd.issuingState)
                    if (countryName) handleChangeDocumentField(idx, 'PASSPORT', 'countryIssued', countryName)
                }

                // Dates — middle-end converts MRZ YYMMDD → DD/MM/YYYY
                // Note: MRZ has no issue date; dateOfIssue is intentionally not handled
                if (pd.dateOfBirth) {
                    const dob = parseMRZDate(pd.dateOfBirth)
                    if (dob) handleChangeTravelerDetails(idx, 'dateOfBirth', dob)
                }
                if (pd.expirationDate) {
                    const exp = parseMRZDate(pd.expirationDate)
                    if (exp) handleChangeDocumentField(idx, 'PASSPORT', 'expiryDate', exp)
                }

                // mrzValid:false means check digits failed — data was still filled in so we
                // keep the preview and let PassportScan show the "review carefully" banner,
                // but swap the toast to a warning so the user knows to double-check.
                // Do NOT throw here — this is a partial success, not a failure.
                if (!pd.mrzValid) {
                    showError(passportScanTranslate('errorMrzInvalid'))
                } else {
                    showSuccess(passportScanTranslate('successScan'), { iconTheme: { primary: 'green', secondary: 'white' } })
                }
            } else {
                // No usable data — throw so PassportScan clears the preview
                const msg = passportScanTranslate('errorExtract')
                showError(msg)
                throw new PassportScanError(msg)
            }
        } catch (error) {
            console.error('Passport scan error:', error)
            // Re-throw so PassportScan.tsx catches it and calls cleanupState().
            // Only show a toast here for unexpected network-level errors — all
            // known failure paths already called showError() before throwing.
            if (!(error instanceof PassportScanError)) {
                showError(passportScanTranslate('errorScanFailed'))
            }
            throw error
        }
    }, [
        countryData,
        handleChangeTravelerDetails,
        handleChangeDocumentField,
        isBirthCountryRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        itineraryId,
        passportScanTranslate,
        showError,
        showSuccess
    ])

    const handleBlurDocumentField = useCallback(
        (travelerIndex: number, documentType: DocumentType, field: string, inputValue: any) => {
            // Only validate the specific field that was changed
            const documentInputs = TravelerDocumentUtils.travelerDocumentToInputsSegregate(documentType)
            const fieldKey = `traveler_${travelerIndex}_${documentType}_${field}`
            const value =
                htmlSanitizer(inputValue) ??
                travelers[travelerIndex]?.documentDetails?.[documentType]?.[field as keyof DocumentDetails]

            // Only validate if this field is required for this document type
            if (documentInputs[field as keyof DocumentDetails]) {
                travelerValidation.validateField(fieldKey, value)
            }
        },
        [travelerValidation, travelers]
    )

    // onChangeContactDetails with validation
    const handleChangeContactDetails: ContactDetailsFormProps['onChange'] = useCallback(
        (key, value) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeContactDetails(key, sanitizedValue)
            const current = contactValidation.validationState?.[key]
            if (current?.isError) {
                contactValidation.clearFieldError(key)
            }
        },
        [onChangeContactDetails, contactValidation]
    )

    const handleBlurContactDetails = useCallback(
        (key: keyof Customer, inputValue: any) => {
            const value = htmlSanitizer(inputValue) ?? contactDetails[key]
            if (key === 'mobile') {
                contactValidation.validateField(key, {
                    mobile: value,
                    mobileCountryCode: contactDetails.mobileCountryCode
                })
            } else if (key === 'mobileCountryCode') {
                contactValidation.validateField(key, value)
                contactValidation.validateField('mobile', {
                    mobile: contactDetails.mobile,
                    mobileCountryCode: value
                })
            } else {
                contactValidation.validateField(key, value)
            }
        },
        [contactValidation, contactDetails]
    )

    // onChangeTaxDetails with validation
    const handleChangeTaxDetails = useCallback(
        (key: keyof TaxDetails, value: string | boolean) => {
            const sanitizedValue = htmlSanitizer(value)
            onChangeTaxDetails(key, sanitizedValue)
            const current = taxValidation.validationState?.[key]
            if (current?.isError) {
                taxValidation.clearFieldError(key)
            }
        },
        [onChangeTaxDetails, taxValidation]
    )

    const handleBlurTaxDetails = useCallback(
        (key: keyof TaxDetails, inputValue: any) => {
            const value = htmlSanitizer(inputValue) ?? taxDetails[key]
            taxValidation.validateField(key, value)
        },
        [taxValidation, taxDetails]
    )

    const [activeTravelerIndexes, setActiveTravelerIndexes] = useState<number[]>([0]) //it's an array because we can expand multiple travelers details at a time for scenario

    const scrollToTraveler = (idx: number) => {
        const upperSiblingElementIndex = idx - 1
        setTimeout(() => {
            const targetElement = document.getElementById(`traveler-${upperSiblingElementIndex}`)
            if (targetElement) {
                targetElement.scrollIntoView({ block: 'start', behavior: 'smooth' })
            }
        }, 500)
    }


    const onSaveTraveler = (idx: number) => {
        const isTravelerValid = validateTraveler(idx)
        const contactIsValid =
            idx === 0
                ? contactValidation.validateAll({
                    email: contactDetails.email,
                    mobile: {
                        mobile: contactDetails.mobile,
                        mobileCountryCode: contactDetails.mobileCountryCode
                    },
                    mobileCountryCode: contactDetails.mobileCountryCode
                })
                : true

        if (isTravelerValid && contactIsValid) {
            setTravelers(prev => {
                const newTravelers = [...prev]
                newTravelers[idx].showSuccess = true
                newTravelers[idx].showError = false
                newTravelers[idx].showNameAtAccordionSummary = true
                return newTravelers
            })
            setActiveTravelerIndexes([idx + 1])
            scrollToTraveler(idx + 1)
            //clevertap event
            triggerItineraryEvent('Air_UI_Action', {
                action_name: 'add_traveller',
                action_type: 'BUTTON'
            })
        } else {
            setTravelers(prev => {
                const newTravelers = [...prev]
                newTravelers[idx].showError = true
                newTravelers[idx].showSuccess = false
                return newTravelers
            })
        }
    }

    const handleExpandTraveler = (idx: number, expanded: boolean) => {
        if (idx < 0 || idx >= travelers.length) return
        setActiveTravelerIndexes(prev => (prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx]))

        if (!expanded) {
            onSaveTraveler(idx)
        } else {
            // scrollToTraveler(idx)
        }
    }

    const onStepClick = useCallback(() => {
        if (!isStepActive && getIsStepCompleted(stepNumber)) {
            dispatch(setActiveStep(stepNumber))
        }
    }, [dispatch, isStepActive, stepNumber])

    const {
        handleUpdateItineraryAndPrepayment,
        handlePrePayment,
        isUpdateItineraryLoading,
        isUpdateItineraryError,
        isUpdateItinerarySuccess,
        isPrePaymentLoading,
        isPrePaymentError,
        isPrePaymentSuccess,
        prePaymentErrorType,
        showCouponExpiry,
        setShowCouponExpiry
    } = useUpdateItineraryAndPrepayment({
        travelers,
        itineraryId,
        contactDetails,
        isDateOfBirthRequired,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isBirthCountryRequired,
        taxDetails,
        deviceType
    })

    const handleContinue = async () => {
        const isAllFormsValid = validateAllForms()

        if (!isAllFormsValid) {
            for (let idx = 0; idx < travelers.length; idx++) {
                const isContactValid = idx === 0 ? contactValidation.isValid : true
                if (validateTraveler(idx) && isContactValid) {
                    setTravelers(prev => {
                        const newTravelers = [...prev]
                        newTravelers[idx].showSuccess = true
                        newTravelers[idx].showError = false
                        newTravelers[idx].showNameAtAccordionSummary = true
                        return newTravelers
                    })
                } else {
                    setTravelers(prev => {
                        const newTravelers = [...prev]
                        newTravelers[idx].showError = true
                        newTravelers[idx].showSuccess = false
                        return newTravelers
                    })
                    setActiveTravelerIndexes([idx])
                    scrollToTraveler(idx)
                    return
                }
            }
            return
        }

        // Check for duplicate names (first + last name combination)
        const nameMap = new Map<string, number[]>()
        travelers.forEach((traveler, idx) => {
            const nameKey = `${traveler.firstName?.trim().toLowerCase()}_${traveler.lastName?.trim().toLowerCase()}`
            if (nameKey && nameKey !== '_') {
                if (!nameMap.has(nameKey)) {
                    nameMap.set(nameKey, [])
                }
                nameMap.get(nameKey)!.push(idx)
            }
        })

        // Find duplicates
        const duplicateGroups: number[][] = []
        nameMap.forEach(indexes => {
            if (indexes.length > 1) {
                duplicateGroups.push(indexes)
            }
        })

        if (duplicateGroups.length > 0) {
            // Get all duplicate traveler indexes
            const allDuplicateIndexes = duplicateGroups.flat()
            const firstDuplicateIndex = duplicateGroups[0][0]

            // Set showError for all duplicate travelers
            setTravelers(prev => {
                const newTravelers = [...prev]
                allDuplicateIndexes.forEach(idx => {
                    newTravelers[idx].showError = true
                    newTravelers[idx].showSuccess = false

                    travelerValidation.setFieldError(`traveler_${idx}_firstName`)
                    travelerValidation.setFieldError(`traveler_${idx}_lastName`)
                })
                return newTravelers
            })

            // Set active traveler to first duplicate and scroll
            setActiveTravelerIndexes(allDuplicateIndexes)
            scrollToTraveler(firstDuplicateIndex)
            showError(validationTranslate('multipleTravelerWithSameNameNotAllowed'))
            return
        }

        saveTravelerDataLocal({
            // contactDetails,
            itineraryId,
            // taxDetails,
            travelers,
            travelersIndexToPassengerTypeAndSerialNumberMap
        })


        try {
            const selectedDocumentTypes = Array.from(
                new Set(travelers.map(traveler => traveler.selectedDocumentType).filter(Boolean))
            ).join(',')
            const timeSpentInTravelerPageInMs = travelerStepEntryTimeRef.current
                ? Date.now() - travelerStepEntryTimeRef.current
                : 0



            triggerItineraryEvent('Air_UI_Action', {
                action_name: 'continue_to_payment_time_measure',
                action_type: 'BUTTON',
                time_spent_in_traveler_page_in_ms: timeSpentInTravelerPageInMs,
                selected_document_type: selectedDocumentTypes || 'N/A'
            })
        } catch (error) {
            console.error(error)
        }


        // ready to update itinerary
        takeScreenshot(itineraryId, 'travelers', flights)
        await handleUpdateItineraryAndPrepayment()
    }

    return {
        isStepActive,
        travelers,
        contactDetails,
        travelersIndexToPassengerTypeAndSerialNumberMap,
        isNationalityRequiredToRenderInUserPersonalDetailsSection,
        isBirthCountryRequired,
        isDateOfBirthRequired,
        taxDetails,
        onChangeTaxDetails: handleChangeTaxDetails,
        onChangeContactDetails: handleChangeContactDetails,
        onChangeTravelerDetails: handleChangeTravelerDetails,
        onChangeDocumentField: handleChangeDocumentField,
        handleBlurTravelerDetails,
        handleBlurDocumentField,
        handleBlurContactDetails,
        handleBlurTaxDetails,
        // Validation states
        travelerValidation,
        contactValidation,
        taxValidation,
        validateAllForms,
        //  traveler validation functions

        validateTraveler,
        onSavedTravelerSelect,
        activeTravelerIndexes,
        handleExpandTraveler,
        setActiveTravelerIndexes,
        onStepClick,
        onSaveTraveler,
        handleContinue,
        isUpdateItineraryLoading,
        isUpdateItineraryError,
        isUpdateItinerarySuccess,
        isPrePaymentLoading,
        isPrePaymentError,
        isPrePaymentSuccess,
        prePaymentErrorType,
        showCouponExpiry,
        setShowCouponExpiry,
        triggerPrepaymentOnly: handlePrePayment,

        //Mandatory SMB Popup
        showMandatorySmbPopUp,
        
        handlePassportScan
    }
}

export default useAddTravelersStepStateManager
